import React, { useState, useEffect } from 'react';
import { Briefcase, Search, Clock, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import ServiceCard from '../components/shared/ServiceCard';
import { servicesAPI, publicAPI } from '../lib/api';
import { Input } from '../components/ui/input';

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, catsRes] = await Promise.all([
          servicesAPI.getAll(selectedCategory !== 'all' ? selectedCategory : undefined),
          publicAPI.getCategories(),
        ]);
        setServices(servicesRes.data);
        setCategories(catsRes.data.services || []);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedCategory]);

  const filteredServices = services.filter((s) =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl flex items-center justify-center">
                <Briefcase className="text-white" size={28} />
              </div>
              <div>
                <h1 className="font-display font-bold text-2xl md:text-3xl text-zinc-900">Services</h1>
                <p className="text-zinc-500">Book professionals instantly</p>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
              <Input
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 bg-zinc-50 border-zinc-200 rounded-full h-11"
                data-testid="search-services-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Pills */}
      <section className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 overflow-x-auto hide-scrollbar">
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all ${
                selectedCategory === 'all'
                  ? 'bg-violet-500 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              data-testid="category-all"
            >
              All Services
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all ${
                  selectedCategory === cat
                    ? 'bg-violet-500 text-white'
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
                data-testid={`category-${cat.toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-video bg-white rounded-2xl skeleton" />
            ))}
          </div>
        ) : filteredServices.length > 0 ? (
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service, idx) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border border-zinc-200">
            <div className="w-20 h-20 mx-auto mb-4 bg-zinc-100 rounded-2xl flex items-center justify-center">
              <Briefcase className="text-zinc-400" size={40} />
            </div>
            <h3 className="font-display text-xl text-zinc-900 mb-2">No Services Found</h3>
            <p className="text-zinc-500 max-w-md mx-auto">
              {searchQuery ? `No services matching "${searchQuery}"` : 'No services in this category yet'}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
